import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ArrowUp, ArrowDown, Hash, Save, X } from 'lucide-react';

const SequenceManager = ({ 
  items = [], 
  isOpen, 
  onClose, 
  onSave, 
  itemDisplayKey = 'name',
  title = 'Manage Display Sequence' 
}) => {
  const [sequences, setSequences] = useState(() => {
    // Initialize sequences based on current display_sequence or array index
    const sequenceMap = {};
    items.forEach((item, index) => {
      sequenceMap[item.id] = item.display_sequence || (index + 1);
    });
    return sequenceMap;
  });
  const [saving, setSaving] = useState(false);

  const handleSequenceChange = (itemId, newSequence) => {
    const sequenceNumber = parseInt(newSequence) || 1;
    setSequences(prev => ({
      ...prev,
      [itemId]: sequenceNumber
    }));
  };

  const moveItem = (itemId, direction) => {
    const currentSequence = sequences[itemId];
    const newSequence = direction === 'up' ? currentSequence - 1 : currentSequence + 1;
    
    if (newSequence < 1 || newSequence > items.length) return;
    
    // Find item that currently has the target sequence
    const conflictingItem = Object.entries(sequences).find(([id, seq]) => seq === newSequence);
    
    if (conflictingItem) {
      // Swap sequences
      setSequences(prev => ({
        ...prev,
        [itemId]: newSequence,
        [conflictingItem[0]]: currentSequence
      }));
    } else {
      setSequences(prev => ({
        ...prev,
        [itemId]: newSequence
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Ensure ALL items are included in the sequences object
      // This fixes the bug where only changed items were being sent
      const completeSequences = {};
      items.forEach((item) => {
        // Use the current sequence from state, or fallback to the item's current display_sequence or index + 1
        completeSequences[item.id] = sequences[item.id] || item.display_sequence || 1;
      });
      
      console.log('🔍 Saving complete sequences:', completeSequences);
      console.log('🔍 Number of packages being sent:', Object.keys(completeSequences).length);
      
      await onSave(completeSequences);
      onClose();
    } catch (error) {
      console.error('Error saving sequences:', error);
    } finally {
      setSaving(false);
    }
  };

  // Sort items by their current sequence for display
  const sortedItems = [...items].sort((a, b) => {
    const seqA = sequences[a.id] || 1;
    const seqB = sequences[b.id] || 1;
    return seqA - seqB;
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hash className="h-5 w-5" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            Set the display order for each item. Lower numbers appear first.
          </div>

          <div className="space-y-3">
            {sortedItems.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50"
              >
                {/* Sequence Input */}
                <div className="flex items-center gap-2 min-w-0">
                  <Label className="text-xs font-medium text-gray-600">
                    Seq:
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    max={items.length}
                    value={sequences[item.id] || 1}
                    onChange={(e) => handleSequenceChange(item.id, e.target.value)}
                    className="w-16 h-8 text-center"
                  />
                </div>

                {/* Move Buttons */}
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => moveItem(item.id, 'up')}
                    disabled={sequences[item.id] <= 1}
                    className="h-8 w-8 p-0"
                  >
                    <ArrowUp className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => moveItem(item.id, 'down')}
                    disabled={sequences[item.id] >= items.length}
                    className="h-8 w-8 p-0"
                  >
                    <ArrowDown className="h-3 w-3" />
                  </Button>
                </div>

                {/* Item Name */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">
                    {item[itemDisplayKey] || 'Untitled'}
                  </div>
                  {item.description && (
                    <div className="text-xs text-gray-500 truncate">
                      {item.description}
                    </div>
                  )}
                </div>

                {/* Current Position Indicator */}
                <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  #{sequences[item.id]}
                </div>
              </div>
            ))}
          </div>

          {/* Preview Order */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Preview Order:</h4>
            <div className="text-sm text-blue-800">
              {sortedItems.map((item, index) => (
                <span key={item.id}>
                  {index + 1}. {item[itemDisplayKey]}
                  {index < sortedItems.length - 1 && ' → '}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Sequence'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SequenceManager;